// frontend/src/components/FileList.tsx - v3.10
import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  fetchAllFiles, 
  searchAllFiles, 
  FileMetadata,
  FileType, 
  getPresignedDownloadUrl,
  getPresignedViewUrl,
  getFileName, 
  formatFileSize 
} from '../services/api';
import { getDisplayFileName, getFileTypeFromExtension } from '../services/FileUtils';
import { recordActivity } from '../services/activityService';
import { 
  Box, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper, 
  Typography, 
  IconButton, 
  CircularProgress,
  Alert,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Button,
  Fade
} from '@mui/material';
import {
  ArrowDownward as ArrowDownwardIcon,
  ArrowUpward as ArrowUpwardIcon,
  Download as DownloadIcon,
  Link as LinkIcon,
  MoreVert as MoreVertIcon,
  InsertDriveFile as FileIcon,
  VideoFile as VideoIcon,
  Description as TextIcon,
  TableChart as CsvIcon,
  AudioFile as AudioIcon,
  Image as ImageIcon,
  PictureAsPdf as PdfIcon,
  PlayArrow as PlayIcon
} from '@mui/icons-material';
import SearchBar, { SearchParams } from './SearchBar';

const debounce = <F extends (...args: any[]) => void>(
  func: F,
  wait: number
): ((...args: Parameters<F>) => void) & { cancel: () => void } => {
  let timeout: ReturnType<typeof setTimeout> | null = null;

  const debounced = (...args: Parameters<F>) => {
    if (timeout !== null) {
      clearTimeout(timeout);
    }
    timeout = setTimeout(() => func(...args), wait);
  };

  debounced.cancel = () => {
    if (timeout !== null) {
      clearTimeout(timeout);
    }
  };

  return debounced;
};

const FileList: React.FC = () => {
  const navigate = useNavigate();
  const [filteredFiles, setFilteredFiles] = useState<FileMetadata[]>([]);
  const [loading, setLoading] = useState(true);
  const [downloadLoading, setDownloadLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: 'asc' | 'desc';
  }>({
    key: 'name',
    direction: 'asc'
  });
  const [searchText, setSearchText] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedFile, setSelectedFile] = useState<FileMetadata | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [visibleFiles, setVisibleFiles] = useState(25); // Initial limit of 25 files
  const observer = useRef<IntersectionObserver | null>(null);

  const loadFiles = useCallback(async (searchQuery: string | null) => {
    setLoading(true);
    try {
      let files: FileMetadata[] = [];
      console.log('Loading files with searchQuery:', searchQuery, 'sortConfig:', sortConfig);
      
      if (searchQuery) {
        const result = await searchAllFiles(
          searchQuery,
          sortConfig.key,
          sortConfig.direction
        );
        console.log('[Debug] Raw searchAllFiles result:', result);
        files = result.files || [];
      } else {
        const result = await fetchAllFiles();
        console.log('[Debug] Raw fetchAllFiles result:', result);
        files = result.files || [];
        console.log('Total files fetched from fetchAllFiles:', files.length);
      }
      
      console.log('API Response (processed):', files);
      setFilteredFiles(files);
      setError(null);
    } catch (err) {
      console.error('Error fetching files:', err);
      setError(`Failed to load files: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setLoading(false);
    }
  }, [sortConfig]);

  const debouncedLoadFiles = useMemo(() => 
    debounce((query: string | null) => loadFiles(query), 300),
    [loadFiles]
  );

  useEffect(() => {
    console.log('Effect triggered with searchText:', searchText, 'retryCount:', retryCount);
    debouncedLoadFiles(searchText);
    return () => debouncedLoadFiles.cancel();
  }, [searchText, retryCount, debouncedLoadFiles]);

  const sortedFiles = useMemo(() => {
    if (!Array.isArray(filteredFiles)) {
      console.error('filteredFiles is not an array:', filteredFiles);
      return [];
    }
    
    const files = [...filteredFiles];
    return files.sort((a, b) => {
      let comparison = 0;
      switch (sortConfig.key) {
        case 'size':
          comparison = a.size - b.size;
          break;
        case 'date':
          const dateA = a.lastModified ? new Date(a.lastModified).getTime() : 0;
          const dateB = b.lastModified ? new Date(b.lastModified).getTime() : 0;
          comparison = dateA - dateB;
          break;
        case 'name':
        default:
          const fileNameA = getDisplayFileName(a.key);
          const fileNameB = getDisplayFileName(b.key);
          comparison = fileNameA.toLowerCase().localeCompare(fileNameB.toLowerCase());
          break;
      }
      return sortConfig.direction === 'desc' ? -comparison : comparison;
    });
  }, [filteredFiles, sortConfig]);

  const handleSort = (key: string) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
    loadFiles(searchText);
  };

  const handleSearch = useCallback((params: SearchParams) => {
    console.log('Search triggered with params:', params);
    setSearchText(params.searchText || null);
    setSortConfig({
      key: params.sortBy || 'name',
      direction: (params.sortDirection || 'asc') as 'asc' | 'desc'
    });
  }, []);

  const formatDate = (dateString: string | null): string => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'N/A';
      const now = new Date();
      const isThisYear = now.getFullYear() === date.getFullYear();
      return isThisYear
        ? `${date.toLocaleString('en-US', { month: 'short' })} ${date.getDate()}`
        : `${date.toLocaleString('en-US', { month: 'short' })} ${date.getDate()}, ${date.getFullYear()}`;
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'N/A';
    }
  };

  const getFileIcon = (file: FileMetadata) => {
    if (file.fileType) {
      switch (file.fileType) {
        case FileType.VIDEO: return <VideoIcon color="primary" />;
        case FileType.CSV: return <CsvIcon color="success" />;
        case FileType.TEXT: return <TextIcon color="info" />;
        case FileType.AUDIO: return <AudioIcon color="secondary" />;
        case FileType.IMAGE: return <ImageIcon color="action" />;
        case FileType.DOCUMENT: return <PdfIcon color="warning" />;
        default: return <FileIcon color="primary" />;
      }
    }
    const extension = file.key.split('.').pop()?.toLowerCase();
    if (['mp4', 'mov', 'webm'].includes(extension || '')) return <VideoIcon color="primary" />;
    if (extension === 'csv') return <CsvIcon color="success" />;
    if (['txt', 'log', 'opus'].includes(extension || '')) return <TextIcon color="info" />;
    if (['mp3', 'wav', 'ogg'].includes(extension || '')) return <AudioIcon color="secondary" />;
    if (['jpg', 'jpeg', 'png', 'gif'].includes(extension || '')) return <ImageIcon color="action" />;
    if (['pdf', 'doc', 'docx'].includes(extension || '')) return <PdfIcon color="warning" />;
    return <FileIcon color="primary" />;
  };

  const isVideoFile = useCallback((file: FileMetadata): boolean => {
    if (file.fileType === FileType.VIDEO) return true;
    return getFileTypeFromExtension(file.key) === FileType.VIDEO;
  }, []);

  const handleActionClick = (event: React.MouseEvent<HTMLButtonElement>, file: FileMetadata) => {
    event.preventDefault();
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedFile(file);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedFile(null);
  };

  const handlePlay = useCallback((event: React.MouseEvent<HTMLLIElement>, file: FileMetadata) => {
    event.preventDefault();
    event.stopPropagation();
    console.log('[Debug] Playing file:', file.key, 'Display name:', getDisplayFileName(file.key));
    recordActivity(file.key, 'played', getFileTypeFromExtension(file.key));
    navigate(`/play/${encodeURIComponent(file.key)}`);
    handleMenuClose();
  }, [navigate]);

  const handleDownload = async (event: React.MouseEvent<HTMLLIElement>, file: FileMetadata) => {
    event.preventDefault();
    event.stopPropagation();
    
    console.log('[Debug] Download key:', file.key, 'Display name:', getDisplayFileName(file.key));
    recordActivity(file.key, 'downloaded', getFileTypeFromExtension(file.key));
    setDownloadLoading(file.key);
    try {
      const response = await getPresignedDownloadUrl(file.key);
      console.log('[Debug] Presigned URL:', response.url);
      const link = document.createElement('a');
      link.href = response.url;
      link.download = response.filename || getFileName(file.key); // Use original name for download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Download error:', err);
      const errorMessage = err instanceof Error ? err.message : String(err);
      alert(`Failed to download file: ${errorMessage}. Click "Retry" to try again.`);
    } finally {
      setDownloadLoading(null);
    }
    handleMenuClose();
  };

  const handleCopyLink = async (event: React.MouseEvent<HTMLLIElement>, file: FileMetadata) => {
    event.preventDefault();
    event.stopPropagation();
    const fileType = file.fileType || '';
    let url = '';
    
    const origin = window.location.origin;
    
    try {
      if (fileType === FileType.CSV) {
        url = `${origin}/view/csv/${encodeURIComponent(file.key)}`;
      } else if (fileType === FileType.TEXT) {
        url = `${origin}/view/text/${encodeURIComponent(file.key)}`;
      } else if (fileType === FileType.VIDEO || file.isViewable) {
        url = `${origin}/play/${encodeURIComponent(file.key)}`;
      } else {
        const response = await getPresignedDownloadUrl(file.key);
        url = response.url;
      }
      
      await navigator.clipboard.writeText(url);
      console.log('URL copied to clipboard:', url);
      alert('Link copied to clipboard');
    } catch (err) {
      console.error('Failed to copy URL:', err);
      const textarea = document.createElement('textarea');
      textarea.value = url;
      document.body.appendChild(textarea);
      textarea.select();
      try {
        document.execCommand('copy');
        alert('Link copied to clipboard');
      } catch (e) {
        console.error('Fallback copy failed:', e);
        alert('Failed to copy link. Please copy it manually: ' + url);
      }
      document.body.removeChild(textarea);
    }
    
    handleMenuClose();
  };

  const handleFileClick = async (file: FileMetadata) => {
    const fileType = file.fileType || getFileTypeFromExtension(file.key);
    if (fileType === FileType.CSV) {
      recordActivity(file.key, 'viewed', FileType.CSV);
      navigate(`/view/csv/${encodeURIComponent(file.key)}`);
    } else if (fileType === FileType.TEXT) {
      recordActivity(file.key, 'viewed', FileType.TEXT);
      navigate(`/view/text/${encodeURIComponent(file.key)}`);
    } else if (fileType === FileType.IMAGE) {
      recordActivity(file.key, 'viewed', FileType.IMAGE);
      navigate(`/view/image/${encodeURIComponent(file.key)}`);
    } else if (fileType === FileType.VIDEO || file.isViewable) {
      recordActivity(file.key, 'played', FileType.VIDEO);
      navigate(`/play/${encodeURIComponent(file.key)}`);
    } else {
      try {
        recordActivity(file.key, 'downloaded', fileType as any);
        const response = await getPresignedDownloadUrl(file.key);
        window.open(response.url, '_blank');
      } catch (err) {
        console.error('Error opening file:', err);
        alert('Failed to open file. Please try downloading it manually.');
      }
    }
  };

  const isFileViewable = (file: FileMetadata): boolean => {
    if (typeof file.isViewable === 'boolean') return file.isViewable;
    const fileType = file.fileType || '';
    return [FileType.VIDEO, FileType.CSV, FileType.TEXT].includes(fileType as FileType);
  };

  const handleRetry = () => {
    setError(null);
    setRetryCount(prev => prev + 1);
  };

  // Fire-and-forget presigned URL pre-fetch on hover.
  // getPresignedViewUrl checks the cache first, so hovering the same row twice
  // costs nothing after the first fetch.
  const handleFileHover = useCallback((key: string) => {
    if (getFileTypeFromExtension(key) === FileType.VIDEO) {
      getPresignedViewUrl(key).catch(() => {});
    }
  }, []);

  const loadMoreRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && visibleFiles < sortedFiles.length) {
        setVisibleFiles(prev => Math.min(prev + 25, sortedFiles.length));
      }
    });
    if (loadMoreRef.current) observer.current.observe(loadMoreRef.current);
    return () => { if (observer.current) observer.current.disconnect(); };
  }, [sortedFiles, visibleFiles]);

  if (loading && sortedFiles.length === 0) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
      <CircularProgress />
    </Box>
  );

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 2 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        My Files
      </Typography>
      
      <SearchBar onSearch={handleSearch} />
      
      {error && (
        <Box sx={{ mt: 2, mb: 2 }}>
          <Alert 
            severity="error" 
            action={
              <Button 
                color="inherit" 
                size="small" 
                onClick={handleRetry}
              >
                Retry
              </Button>
            }
          >
            {error}
          </Alert>
        </Box>
      )}
      
      <Fade in={true} timeout={500}>
        <TableContainer component={Paper} sx={{ mt: 2 }}>
          <Table sx={{ minWidth: { xs: '100%', sm: 650 } }} aria-label="file list">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell 
                  onClick={() => handleSort('name')}
                  sx={{ 
                    fontWeight: 'bold', 
                    cursor: 'pointer',
                    minWidth: '400px',
                    '&:hover': { backgroundColor: '#e0e0e0' }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    Name
                    {sortConfig.key === 'name' && (
                      sortConfig.direction === 'asc' 
                        ? <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                        : <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                    )}
                  </Box>
                </TableCell>
                <TableCell 
                  onClick={() => handleSort('date')}
                  align="right"
                  sx={{ 
                    fontWeight: 'bold', 
                    cursor: 'pointer',
                    minWidth: '150px',
                    '&:hover': { backgroundColor: '#e0e0e0' }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                    Last modified
                    {sortConfig.key === 'date' && (
                      sortConfig.direction === 'asc' 
                        ? <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                        : <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                    )}
                  </Box>
                </TableCell>
                <TableCell 
                  onClick={() => handleSort('size')}
                  align="right"
                  sx={{ 
                    fontWeight: 'bold', 
                    cursor: 'pointer',
                    minWidth: '100px',
                    '&:hover': { backgroundColor: '#e0e0e0' }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                    File size
                    {sortConfig.key === 'size' && (
                      sortConfig.direction === 'asc' 
                        ? <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                        : <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                    )}
                  </Box>
                </TableCell>
                <TableCell align="center" sx={{ width: '80px' }}>
                  <MoreVertIcon fontSize="small" />
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedFiles.slice(0, visibleFiles).length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    {loading ? 'Loading...' : 'No files found'}
                  </TableCell>
                </TableRow>
              ) : (
                sortedFiles.slice(0, visibleFiles).map((file) => (
                  <TableRow
                    key={file.key}
                    sx={{ '&:hover': { backgroundColor: '#f5f5f5' } }}
                    onClick={() => handleFileClick(file)}
                    onMouseEnter={() => handleFileHover(file.key)}
                    style={{ cursor: isFileViewable(file) ? 'pointer' : 'default' }}
                  >
                    <TableCell component="th" scope="row">
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {getFileIcon(file)}
                        <Tooltip 
                          title={isFileViewable(file) 
                            ? `Click to view ${getDisplayFileName(file.key)}${file.key.includes('ProcessLog.csv') ? ' (Process Log)' : ''}` 
                            : `Click to download ${getDisplayFileName(file.key)} (Not viewable in browser)`
                          }
                        >
                          <Typography sx={{ ml: 2, color: 'text.primary' }}>
                            {getDisplayFileName(file.key)}
                          </Typography>
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      {formatDate(file.lastModified)}
                    </TableCell>
                    <TableCell align="right">
                      {formatFileSize(file.size)}
                    </TableCell>
                    <TableCell align="center">
                      {downloadLoading === file.key ? (
                        <CircularProgress size={20} />
                      ) : (
                        <IconButton 
                          size="small"
                          onClick={(e) => handleActionClick(e, file)}
                          aria-label={`Actions for ${getDisplayFileName(file.key)}`}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Fade>
      
      {loading && sortedFiles.length > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <CircularProgress />
        </Box>
      )}
      
      {visibleFiles < sortedFiles.length && (
        <Box ref={loadMoreRef} sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Button variant="contained" onClick={() => setVisibleFiles(prev => prev + 25)}>Load More</Button>
        </Box>
      )}
      
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {selectedFile && (
          <>
            {/* Only show Play option for video files */}
            {isVideoFile(selectedFile) && (
              <MenuItem onClick={(e) => handlePlay(e, selectedFile)} disabled={downloadLoading === selectedFile.key}>
                <ListItemIcon>
                  <PlayIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Play</ListItemText>
              </MenuItem>
            )}
            <MenuItem onClick={(e) => handleDownload(e, selectedFile)} disabled={downloadLoading === selectedFile.key}>
              <ListItemIcon>
                <DownloadIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Download</ListItemText>
            </MenuItem>
            <MenuItem onClick={(e) => handleCopyLink(e, selectedFile)}>
              <ListItemIcon>
                <LinkIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Copy Link</ListItemText>
            </MenuItem>
          </>
        )}
      </Menu>
    </Box>
  );
};

export default FileList;